import {
    Button,
    FormControl,
    Stack,
    TextField,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Box,
    Tooltip,
    IconButton,
    Zoom,
    Paper,
    Typography,
    Chip,
    Divider,
    alpha,
    Avatar
} from '@mui/material';
import {
    Settings as SettingsIcon,
    AutoFixHigh as MagicIcon,
    SmartToy as BotIcon,
    Psychology as PersonaIcon,
    Description as DescriptionIcon,
    Memory as ModelIcon,
    RecordVoiceOver as EncouragementIcon,
    Rule as InstructionIcon,
    ArrowBack as BackIcon,
    Save as SaveIcon,
    Publish as PublishIcon,
    Refresh as ResetIcon,
    QuestionAnswer,
    RecordVoiceOver,
    Escalator as EscalationIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';

import { fetchChatBots, fetchMyAssistants } from '../../../store/slices/ChatBotsSlice';
import { setSelectedAgent } from '../../../store/slices/SelectedAgentSlice';
import MultiItemInput from '../../../components/MultiItemInput';
import ApiUrls from '../../../helper/ApiUrls';
import { API } from '../../../services/axios.service';
import { AccessKeySkeleton } from '../../settings/inc/Skeletons';
import { AccessModel } from '../../../helper';

const PlaygroundForm = ({ agent }) => {
    const dispatch = useDispatch();
    const llms = useSelector((state) => state.llm.data);
    const accessKeyData = useSelector((state) => state.accessKeys.data);
    const { enqueueSnackbar } = useSnackbar();
    const [resetAutoCompletes, setResetAutoCompletes] = useState(0);
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const stateData = location.state;
    const [loading, setLoading] = useState('');

    // const [agent,setAgent] = useState(null);

    // Default escalation rules
    const defaultEscalationRules = "You must always stay within your scope. If a query is out of your scope, immediately escalate the conversation by generating a comprehensive summary and sending an email to the supervisor using the send_mail_to_supervisor tool. The parameter conversation_summary must include a detailed summary of the conversation.\n\nCritical Escalation Cases (Act on these immediately):\n1. If the conversation involves sensitive or confidential information: If the user asks for personal, legal, financial, or medical advice that cannot be safely provided by you, escalate the case immediately.\n2. If the user's request requires human intervention: If you cannot resolve the technical issue or provide the necessary response, escalate it right away to ensure the user receives appropriate help.\n3. If the user has made multiple unsuccessful attempts to get assistance: If the user has attempted to resolve an issue repeatedly without success, escalate the case immediately to ensure proper support is provided.\n4. If the requested task exceeds the capabilities of the tools available: If the user requests a task requiring tools or actions not listed in the tool_names, escalate immediately.\n5. If the user asks for help that violates company policies or ethical guidelines: If the user's request involves unethical actions (e.g., hacking, fraud), escalate immediately and do not attempt to fulfill the request.\n6. If the task requires personal judgment or opinions: If the user's request calls for personal judgment, advice, or opinions outside the predefined knowledge bases, escalate immediately.\n7. If the request involves highly specialized or domain-specific knowledge: If the user asks about highly technical, domain-specific topics (e.g., advanced AI techniques or specialized programming), escalate to a supervisor with the required expertise.\n8. If there is ambiguity or confusion in understanding the user's request: If you encounter an unclear or ambiguous request that you cannot resolve, escalate immediately for clarification.\n9. If there is a system error or failure: If a system or tool failure (e.g., tool timeout, API failure) prevents you from completing the user's request, escalate it to the supervisor for troubleshooting.\n10. If there are repeated technical issues or limitations: If you encounter persistent errors or limitations in your tools that prevent you from helping the user, escalate to the supervisor immediately.";

    const botSchema = Yup.object().shape({
        name: Yup.string()
            .required('Name required')
            .matches(/^[A-Za-z ]*$/, 'Please enter valid name'),
        persona_name: Yup.string()
            .required('Agent Role Name required')
            .matches(/^[A-Za-z ]*$/, 'Please enter valid name'),
        description: Yup.string().required('Description required'),
        encouragement: Yup.string().required('Encouragement text required'),
        instruction: Yup.string().required('Instruction text required'),
        escalation_rules: Yup.string().required('Escalation rules required'),
        llm_id: Yup.string().required('Model is required'),
        sample_prompts: Yup.array()
            .of(Yup.string().required('Prompt is required'))
            .min(1, 'At least one prompt is required')
            .max(4, 'You can add up to 4 prompts only'),
    });

    const defaultValues = {
        name: agent?.name,
        persona_name: agent?.persona_name,
        description: agent?.description,
        encouragement: agent?.encouragement,
        instruction: agent?.instruction,
        escalation_rules: agent?.escalation_rules || defaultEscalationRules,
        llm_id: agent?.llm_id,
        sample_prompts: agent?.sample_prompts || [],
    };

    const updateStatusHandler = () => {
        const payload = {
            status: "public",
            id: agent.id
        };

        API.put(ApiUrls.BOT_RESOURCE, payload)
            .then(() => {
                enqueueSnackbar("Agent published successfully! 🚀", {
                    variant: 'success',
                });
                dispatch(fetchChatBots({ page: 1, limit: 10, forceReload: true }));
                dispatch(fetchMyAssistants({ page: 1, limit: 10, forceReload: true }));
                setResetAutoCompletes(resetAutoCompletes + 1);
            })
            .catch((err) => {
                enqueueSnackbar(err.response?.data?.message || err.message, {
                    variant: 'error',
                });
            });
    };

    const addEditBot = (values, { setSubmitting, setErrors }) => {
        setSubmitting(true);
        const payload = {};
        Object.entries(values).forEach(([key, val]) => {
            if (String(val).trim().length > 0) {
                payload[key] = val;
            }
        });
        payload.id = agent.id;

        API.put(ApiUrls.BOT_RESOURCE, payload)
            .then(() => {
                enqueueSnackbar('Agent updated successfully! ✨', {
                    variant: 'success',
                });
                dispatch(setSelectedAgent(payload));
                setResetAutoCompletes(resetAutoCompletes + 1);
            })
            .catch((err) => {
                if (err.response?.status === 403 || err.response?.status === 422) {
                    enqueueSnackbar(err.response?.data?.message || err.message, {
                        variant: 'error',
                    });
                } else {
                    enqueueSnackbar(err.response?.data?.message || err.message, {
                        variant: 'error',
                    });
                }
                if (err.response?.data?.errors?.json) {
                    setErrors(err.response.data.errors.json);
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const botForm = useFormik({
        initialValues: defaultValues,
        validationSchema: botSchema,
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: addEditBot,
    });

    const handleAutoFieldFillUp = (fieldName) => {
        setLoading(fieldName);
        const payload = {
            "role_name": botForm.values.name,
            "persona": botForm.values.persona_name,
            "description": botForm.values.description,
            "is_instruction": fieldName === 'instruction'
        };

        if (fieldName === 'default') {
            delete payload.is_instruction;
        }

        const response = API.post(
            fieldName === 'default' ? ApiUrls.DEFAULT_PROMPT : ApiUrls.PERSONA_PROMPT,
            payload
        );

        response.then((res) => {
            if (fieldName === 'instruction') {
                botForm.setFieldValue('instruction', res?.data?.data?.result);
            } else if (fieldName === 'encouragement') {
                botForm.setFieldValue('encouragement', res?.data?.data?.result);
            } else {
                botForm.setFieldValue('sample_prompts', res?.data?.data?.result);
            }
            enqueueSnackbar('AI generated content added! 🤖', { variant: 'success' });
        })
            .catch(() => {
                enqueueSnackbar('Failed to generate content. Please try again.', { variant: 'error' });
            })
            .finally(() => {
                setLoading('');
            });
    };

    const resetBotForm = () => {
        botForm.handleReset();
        setResetAutoCompletes(resetAutoCompletes + 1);
        enqueueSnackbar('Form reset successfully', { variant: 'info' });
    };

    const MagicButton = ({ onClick, disabled, loading: isLoading, tooltip, customTop }) => (
        <Tooltip title={tooltip} arrow placement="left">
            <IconButton
                disabled={disabled || loading}
                onClick={onClick}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: customTop || 8,
                    width: 28,
                    height: 28,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    zIndex: 10,
                    '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                        transform: 'scale(1.05)'
                    },
                    '&:disabled': {
                        background: alpha('#667eea', 0.3),
                        color: alpha('#fff', 0.5)
                    },
                    display: isLoading ? 'none' : 'flex'
                }}
            >
                <MagicIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
    
    MagicButton.propTypes = {
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
        loading: PropTypes.bool,
        tooltip: PropTypes.any,
        customTop: PropTypes.any
    };


    useEffect(() => {
        if (agent && botForm) {
            botForm.setValues({
                name: agent?.name,
                persona_name: agent?.persona_name,
                description: agent?.description,
                encouragement: agent?.encouragement,
                instruction: agent?.instruction,
                escalation_rules: agent?.escalation_rules || defaultEscalationRules,
                llm_id: agent?.llm_id,
                sample_prompts: agent?.sample_prompts || [],
            });
        }
    }, [agent, botForm]);

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box sx={{
                p: 3,
                pb: 2,
                flexShrink: 0
            }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {stateData?.backButton && (
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/my-agents/chat-bots/${agent.id}`, { state: { backButton: true } })}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'primary.main',
                                    bgcolor: alpha('#667eea', 0.1)
                                }
                            }}
                        >
                            <BackIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <SettingsIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Configuration Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Customize your AI agent's behavior and responses
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            <Divider />

            {/* Scrollable Content */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                p: 3,
                '&::-webkit-scrollbar': {
                    width: 6
                },
                '&::-webkit-scrollbar-track': {
                    background: alpha('#000', 0.05)
                },
                '&::-webkit-scrollbar-thumb': {
                    background: alpha('#000', 0.2),
                    borderRadius: 3
                }
            }}>
                <Stack spacing={3}>
                    {/* Basic Information Section */}
                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#667eea', 0.02) }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <BotIcon color="primary" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Basic Information
                            </Typography>
                        </Stack>

                        <Stack spacing={2.5}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                id="name"
                                name="name"
                                value={botForm.values.name}
                                onChange={botForm.handleChange}
                                error={botForm.errors.name && botForm.touched.name}
                                helperText={botForm.touched.name ? botForm.errors?.name : undefined}
                                placeholder="Enter agent name"
                                label="Agent Name"
                                disabled={botForm.isSubmitting}
                                InputProps={{
                                    startAdornment: <BotIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                }}
                            />

                            <TextField
                                fullWidth
                                required
                                size="small"
                                id="persona_name"
                                name="persona_name"
                                value={botForm.values.persona_name}
                                onChange={botForm.handleChange}
                                error={botForm.errors.persona_name && botForm.touched.persona_name}
                                helperText={botForm.touched.persona_name ? botForm.errors?.persona_name : undefined}
                                placeholder="Enter role name (e.g., Customer Support Agent)"
                                label="Agent Role Name"
                                disabled={botForm.isSubmitting}
                                InputProps={{
                                    startAdornment: <PersonaIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                }}
                            />

                            <TextField
                                fullWidth
                                required
                                size="small"
                                id="description"
                                name="description"
                                value={botForm.values.description}
                                onChange={botForm.handleChange}
                                error={!!botForm.errors.description && botForm.touched.description}
                                helperText={botForm.touched.description ? botForm.errors?.description : "Brief description of your agent's purpose"}
                                placeholder="Describe what this agent does"
                                label="Description"
                                multiline
                                rows={3}
                                disabled={botForm.isSubmitting}
                                InputProps={{
                                    startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20, alignSelf: 'flex-start', mt: 0.5 }} />
                                }}
                            />
                        </Stack>
                    </Paper>

                    {/* Model Selection */}
                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#4caf50', 0.02) }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <ModelIcon color="success" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                AI Model Configuration
                            </Typography>
                        </Stack>

                        <FormControl fullWidth size="small" error={!!botForm.errors.llm_id && botForm.touched.llm_id} required>
                            <InputLabel>Choose AI Model</InputLabel>
                            <Select
                                id="llm_id"
                                name="llm_id"
                                value={String(botForm.values.llm_id)}
                                label="Choose AI Model"
                                onChange={botForm.handleChange}
                                onBlur={botForm.handleBlur}
                                startAdornment={<ModelIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
                            >
                                {llms.map((llm, i) => (
                                    <MenuItem
                                        key={i}
                                        value={llm.id}
                                        sx={{
                                            bgcolor: 'transparent',
                                            py: 1.5
                                        }}
                                        selected={String(llm.id)===String(botForm.values.llm_id)}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                {llm.name}
                                            </Typography>
                                            <Chip
                                                label={llm.origin}
                                                size="small"
                                                color={'success'}
                                                variant="outlined"
                                            />
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{botForm.touched.llm_id ? botForm.errors.llm_id : 'Select the AI model that will power your agent'}</FormHelperText>
                        </FormControl>
                    </Paper>

                    {/* Behavior Configuration */}
                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#ff9800', 0.02) }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <RecordVoiceOver color="warning" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Behavior & Personality
                            </Typography>
                        </Stack>

                        <Stack spacing={3}>
                            {/* Encouragement Field */}
                            <Box sx={{ position: 'relative' }}>
                                <MagicButton
                                    onClick={() => handleAutoFieldFillUp('encouragement')}
                                    disabled={!botForm.values.name || !botForm.values.persona_name || !botForm.values.description}
                                    loading={loading === 'encouragement'}
                                    tooltip="Generate encouragement text using AI"
                                />

                                {loading === 'encouragement' ? (
                                    <AccessKeySkeleton />
                                ) : (
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        id="encouragement"
                                        name="encouragement"
                                        value={botForm.values.encouragement}
                                        onChange={botForm.handleChange}
                                        error={!!botForm.errors.encouragement && botForm.touched.encouragement}
                                        helperText={botForm.touched.encouragement ? botForm.errors?.encouragement : "Define the agent's role and personality (e.g., 'You are a helpful assistant...')"}
                                        placeholder="Define your agent's role and personality"
                                        label="Role Encouragement"
                                        multiline
                                        minRows={3}
                                        maxRows={8}
                                        disabled={botForm.isSubmitting}
                                        InputProps={{
                                            startAdornment: <EncouragementIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20, alignSelf: 'flex-start', mt: 0.5 }} />,
                                            inputComponent: TextareaAutosize
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                pr: 5, // Make room for magic button
                                                resize: 'vertical'
                                            }
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Instruction Field */}
                            <Box sx={{ position: 'relative' }}>
                                <MagicButton
                                    onClick={() => handleAutoFieldFillUp('instruction')}
                                    disabled={!botForm.values.name || !botForm.values.persona_name || !botForm.values.description}
                                    loading={loading === 'instruction'}
                                    tooltip="Generate instruction text using AI"
                                />

                                {loading === 'instruction' ? (
                                    <AccessKeySkeleton />
                                ) : (
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        id="instruction"
                                        name="instruction"
                                        value={botForm.values.instruction}
                                        onChange={botForm.handleChange}
                                        error={!!botForm.errors.instruction && botForm.touched.instruction}
                                        helperText={botForm.touched.instruction ? botForm.errors?.instruction : "Specific rules and guidelines for the agent's behavior"}
                                        placeholder="Set specific rules and guidelines for your agent"
                                        label="Behavioral Instructions"
                                        multiline
                                        minRows={3}
                                        maxRows={8}
                                        disabled={botForm.isSubmitting}
                                        InputProps={{
                                            startAdornment: <InstructionIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20, alignSelf: 'flex-start', mt: 0.5 }} />,
                                            inputComponent: TextareaAutosize
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                pr: 5, // Make room for magic button
                                                resize: 'vertical'
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Escalation Rules Section */}
                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#f44336', 0.02) }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <EscalationIcon sx={{ color: '#f44336' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Escalation Rules
                            </Typography>
                        </Stack>

                        <TextField
                            fullWidth
                            required
                            size="small"
                            id="escalation_rules"
                            name="escalation_rules"
                            value={botForm.values.escalation_rules}
                            onChange={botForm.handleChange}
                            error={!!botForm.errors.escalation_rules && botForm.touched.escalation_rules}
                            helperText={botForm.touched.escalation_rules ? botForm.errors?.escalation_rules : "Define when and how the agent should escalate conversations to human supervisors"}
                            placeholder="Define escalation rules for complex or sensitive situations"
                            label="Escalation Rules"
                            multiline
                            minRows={6}
                            maxRows={12}
                            disabled={botForm.isSubmitting}
                            InputProps={{
                                startAdornment: <EscalationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20, alignSelf: 'flex-start', mt: 0.5 }} />,
                                inputComponent: TextareaAutosize
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    resize: 'vertical'
                                }
                            }}
                        />
                    </Paper>

                    {/* Sample Prompts */}
                    <Paper sx={{ p: 3, borderRadius: 2, bgcolor: alpha('#9c27b0', 0.02) }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <QuestionAnswer sx={{ color: '#9c27b0' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Sample Prompts
                            </Typography>
                        </Stack>

                        <Box sx={{ position: 'relative' }}>
                            {loading === 'default' ? (
                                <AccessKeySkeleton />
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Add 1-4 sample questions that users can ask your agent
                                    </Typography>
                                    <Box sx={{ position: 'relative' }}>
                                        <MagicButton
                                            onClick={() => handleAutoFieldFillUp('default')}
                                            disabled={!botForm.values.name || !botForm.values.persona_name || !botForm.values.description}
                                            loading={loading === 'default'}
                                            tooltip="Generate sample prompts using AI"
                                            customTop={6}
                                        />
                                        <MultiItemInput
                                            required
                                            plusIconLeftPadding={'20px'}
                                            inputValues={botForm.values.sample_prompts}
                                            setInputValues={(values) => botForm.setFieldValue('sample_prompts', values)}
                                            error={botForm.errors.sample_prompts}
                                            touched={botForm.touched.sample_prompts}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Stack>
            </Box>

            {/* Fixed Footer */}
            <Box sx={{
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: alpha('#f5f5f5', 0.5)
                }}>
                    <Button
                        disabled={botForm.isSubmitting || loading}
                        onClick={resetBotForm}
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ResetIcon />}
                        sx={{ borderRadius: 1.5 }}
                    >
                        Reset
                    </Button>

                    <Stack direction="row" spacing={1}>
                        <LoadingButton
                            type="button"
                            size="small"
                            variant="outlined"
                            disabled={botForm.isSubmitting || loading}
                            onClick={updateStatusHandler}
                            startIcon={<PublishIcon />}
                            sx={{ borderRadius: 1.5 }}
                        >
                            Publish
                        </LoadingButton>
                        <LoadingButton
                            type="button"
                            size="small"
                            variant="contained"
                            onClick={botForm.handleSubmit}
                            disabled={botForm.isSubmitting || loading}
                            loading={botForm.isSubmitting}
                            startIcon={!botForm.isSubmitting ? <SaveIcon /> : null}
                            sx={{
                                borderRadius: 1.5,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                                }
                            }}
                        >
                            Save Changes
                        </LoadingButton>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

PlaygroundForm.propTypes = {
    agent: PropTypes.object,
};

export default PlaygroundForm;